import React from "react";
import { useLocation } from "react-router-dom";
import { NavBar } from "../NavBar";
import { PageWrapperProps } from "./PageWrapper.types";
import { ROUTES } from "../../../types/routes";
// 直接具体路径，不走 hooks / utils barrel，否则会把整套 hooks（useQueuedPopup 等）
// 拖进 Base → PageWrapper 引入链，产生跨模块巨型循环依赖
import { usePageConfig } from "../../../hooks/usePageConfig";
import "./PageWrapper.scss";
import { BackIcon } from "../../Icon/IconSvgMap";
import { useNavigation } from "../../../utils/navigation";
import { useT } from "../../../i18n/hooks";

export const PageWrapper: React.FC<PageWrapperProps> = ({
  children,
  title,
  showNavBar = true,
  rightComponent,
  className = "",
  testId,
}) => {
  const { goBack } = useNavigation();
  const pageConfig = usePageConfig();
  const location = useLocation();
  const t = useT();

  // 路由匹配对尾斜杠不敏感（React Router 会照常渲染 /x 与 /x/），但 pageConfig 是按 pathname
  // 精确字符串取值——带尾斜杠的 URL（部分部署/SW 重写会补上）会漏配，标题/图标退化成 "Page"。
  // 这里统一去掉结尾斜杠再查表（根路径 "/" 保底），与路由的宽松匹配对齐。
  const normalizedPathname = location.pathname.replace(/\/+$/, "") || "/";

  // 判断是否为首页
  const isHomePage =
    normalizedPathname === ROUTES.HOME || normalizedPathname === ROUTES.HOME_ALT;

  // 如果不需要显示NavBar或者是首页，直接返回children
  if (!showNavBar || isHomePage) {
    return <>{children}</>;
  }

  // 根据路由自动设置标题（救援金规则说明页从 location.state 取 ruleTitle，显示在顶部）
  const getPageTitle = () => {
    if (title) return title;
    if (normalizedPathname === ROUTES.RESCUE_FUND_RULE) {
      const state = location.state as { ruleTitle?: string } | undefined;
      if (state?.ruleTitle) return state.ruleTitle;
    }
    return pageConfig[normalizedPathname]?.title || t("navigation.page");
  };
  const getPageIcon = () => {
    if (rightComponent) return rightComponent;
    return pageConfig[normalizedPathname]?.icon;
  };

  const handleBack = () => {
    goBack();
  };

  // 渲染右侧组件
  const renderRightComponent = () => {
    if (getPageIcon()) {
      return getPageIcon();
    }
    return <></>;
  };

  const isInviteFriendsPage = normalizedPathname === ROUTES.INVITE_FRIENDS;
  const isEventCenterPage = normalizedPathname === ROUTES.EVENT_CENTER;
  const isRewardsPage = normalizedPathname === ROUTES.VIP;
  // 与底部 Tab 对应的根页：刷新后无 navigation state，仍按路径隐藏返回
  const hideBackButtonForTabRoot =
    isInviteFriendsPage || isEventCenterPage || isRewardsPage;

  return (
    <div
      className={`page-wrapper ${className}`}
      data-testid={testId}
    >
      <NavBar
        title={getPageTitle()}
        back={hideBackButtonForTabRoot ? null : true}
        backIcon={hideBackButtonForTabRoot ? false : <BackIcon />}
        onBack={hideBackButtonForTabRoot ? undefined : handleBack}
        right={renderRightComponent()}
        className="page-wrapper__navbar"
        variant="gradient"
      />
      <div className="page-wrapper__content">{children}</div>
    </div>
  );
};

export default PageWrapper;
